import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TemplateModal.css';

const TemplateModal = ({ isOpen, onClose, onSendTemplate, phoneNumber }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState({});
  const [sending, setSending] = useState(false);
  const [manualTemplateName, setManualTemplateName] = useState('');
  const [manualLanguage, setManualLanguage] = useState('es');
  const [useManual, setUseManual] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Limpiar mensajes anteriores
      setUseManual(false); // Resetear opción manual
      setSelectedTemplate(null); // Limpiar selección
      setTemplateParams({}); // Limpiar parámetros
      
      console.log('📋 Cargando plantillas...');
      const response = await axios.get('/api/messages/templates');
      console.log('📋 Respuesta recibida:', response.data);
      
      // Verificar si la respuesta tiene success
      if (response.data && response.data.success === true) {
        const loadedTemplates = response.data.templates || [];
        console.log(`✅ Plantillas cargadas: ${loadedTemplates.length}`);
        setTemplates(loadedTemplates);
        if (loadedTemplates.length === 0) {
          // Si no hay plantillas, mostrar mensaje informativo
          setMessage({ 
            type: 'info', 
            text: 'No se encontraron plantillas. Puedes ingresar el nombre de tu plantilla manualmente.' 
          });
        } else {
          // Si hay plantillas, limpiar mensajes de error
          setMessage({ type: '', text: '' });
          console.log('✅ Plantillas mostradas en el modal');
        }
      } else {
        // Si success es false o no existe, hay un error
        let errorMsg = 'Error desconocido';
        if (response.data) {
          console.error('❌ Error en respuesta:', response.data);
          if (typeof response.data.error === 'string') {
            errorMsg = response.data.error;
          } else if (response.data.error?.message) {
            errorMsg = response.data.error.message;
          } else if (response.data.error) {
            errorMsg = JSON.stringify(response.data.error);
          }
        }
        console.error('❌ Error cargando plantillas:', errorMsg);
        setMessage({ 
          type: 'warning', 
          text: `No se pudieron cargar plantillas: ${errorMsg}. Puedes ingresar el nombre de tu plantilla manualmente.` 
        });
        setTemplates([]); // Asegurar que templates esté vacío
      }
    } catch (error) {
      console.error('❌ Error cargando plantillas (catch):', error);
      console.error('   Response:', error.response);
      console.error('   Data:', error.response?.data);
      
      let errorMsg = 'Error desconocido';
      
      if (error.response) {
        // Error de respuesta del servidor
        if (error.response.data) {
          if (typeof error.response.data.error === 'string') {
            errorMsg = error.response.data.error;
          } else if (error.response.data.error?.message) {
            errorMsg = error.response.data.error.message;
          } else if (error.response.data.error) {
            errorMsg = JSON.stringify(error.response.data.error);
          } else if (error.response.data.message) {
            errorMsg = error.response.data.message;
          }
        } else {
          errorMsg = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      console.error('❌ Mensaje de error final:', errorMsg);
      setMessage({ 
        type: 'warning', 
        text: `No se pudieron cargar plantillas: ${errorMsg}. Puedes ingresar el nombre de tu plantilla manualmente.` 
      });
      setTemplates([]); // Asegurar que templates esté vacío
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Inicializar parámetros vacíos
    const params = {};
    if (template.components) {
      template.components.forEach((component, index) => {
        if (component.type === 'BODY' && component.example) {
          component.example.body_text?.forEach((example, i) => {
            params[`body_${i}`] = '';
          });
        }
      });
    }
    setTemplateParams(params);
  };

  const handleSend = async () => {
    if (!phoneNumber) {
      alert('Por favor ingresa un número de teléfono');
      return;
    }

    let templateName, language, components = [];

    if (useManual) {
      if (!manualTemplateName.trim()) {
        alert('Por favor ingresa el nombre de la plantilla');
        return;
      }
      templateName = manualTemplateName.trim();
      language = manualLanguage;
    } else {
      if (!selectedTemplate) {
        alert('Por favor selecciona una plantilla');
        return;
      }
      
      // Advertir si la plantilla es solo para números de prueba
      if (selectedTemplate.category === 'MARKETING') {
        const confirmSend = window.confirm(
          '⚠️ ADVERTENCIA\n\n' +
          'Esta plantilla solo puede enviarse a números de prueba públicos.\n\n' +
          'Si estás usando un número de producción, el envío fallará.\n\n' +
          '¿Deseas continuar de todas formas?'
        );
        if (!confirmSend) {
          return;
        }
      }
      
      // Advertir si la plantilla está pendiente
      if (selectedTemplate.status === 'PENDING') {
        const confirmSend = window.confirm(
          '⚠️ ADVERTENCIA\n\n' +
          'Esta plantilla está pendiente de aprobación.\n\n' +
          'Puede que no esté disponible para envío hasta que sea aprobada.\n\n' +
          '¿Deseas continuar de todas formas?'
        );
        if (!confirmSend) {
          return;
        }
      }
      
      templateName = selectedTemplate.name;
      // Usar el código de idioma de la plantilla, o convertir 'es' a formato correcto
      language = selectedTemplate.language || 'es';
      // Asegurar que el formato sea correcto (es -> es, en_US -> en_US)
      if (language === 'es' && selectedTemplate.language) {
        language = selectedTemplate.language; // Mantener el formato original
      }
      
      // Construir componentes para la plantilla
      if (selectedTemplate.components) {
        selectedTemplate.components.forEach((component) => {
          if (component.type === 'BODY' && component.example) {
            const bodyParams = [];
            component.example.body_text?.forEach((example, i) => {
              if (templateParams[`body_${i}`]) {
                bodyParams.push({
                  type: 'text',
                  text: templateParams[`body_${i}`]
                });
              }
            });
            if (bodyParams.length > 0) {
              components.push({
                type: 'body',
                parameters: bodyParams
              });
            }
          }
        });
      }
    }

    try {
      setSending(true);
      await onSendTemplate(templateName, language, components);
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.helpfulMessage || 
                      error.response?.data?.error?.message || 
                      error.message;
      alert('Error al enviar plantilla: ' + errorMsg);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="template-modal-overlay" onClick={onClose}>
      <div className="template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-modal-header">
          <h2>📋 Enviar Plantilla de WhatsApp</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="template-modal-content">
          {message.text && (
            <div className={`template-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="template-loading">Cargando plantillas...</div>
          ) : templates.length === 0 ? (
            <>
              {/* Solo mostrar opción manual si no hay plantillas */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useManual}
                    onChange={(e) => setUseManual(e.target.checked)}
                  />
                  <span>Ingresar nombre de plantilla manualmente</span>
                </label>
              </div>

              {useManual ? (
                <div className="manual-template-input">
                  <div className="form-group">
                    <label>Nombre de la plantilla *</label>
                    <input
                      type="text"
                      value={manualTemplateName}
                      onChange={(e) => setManualTemplateName(e.target.value)}
                      placeholder="Ejemplo: bienvenida, confirmacion, etc."
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e9edef' }}
                    />
                    <small style={{ color: '#667781', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Ingresa el nombre exacto de tu plantilla aprobada (sin espacios, usa guiones bajos)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Idioma</label>
                    <select
                      value={manualLanguage}
                      onChange={(e) => setManualLanguage(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e9edef' }}
                    >
                      <option value="es">Español (es)</option>
                      <option value="en">Inglés (en)</option>
                      <option value="pt">Portugués (pt)</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="template-empty">
                  <p>No hay plantillas disponibles automáticamente</p>
                  <p className="template-help">
                    Activa la opción "Ingresar nombre de plantilla manualmente" arriba para usar tus plantillas
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Mostrar plantillas automáticamente si hay disponibles */}
              <div className="template-list">
                <h3>Selecciona una plantilla:</h3>
                <div className="templates-grid">
                  {templates.map((template) => (
                    <div
                      key={template.name}
                      className={`template-card ${selectedTemplate?.name === template.name ? 'selected' : ''}`}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="template-name">{template.name}</div>
                      <div className="template-status">
                        {template.status === 'APPROVED' ? '✅ Aprobada' : 
                         template.status === 'PENDING' ? '⏳ Pendiente' : 
                         template.status === 'REJECTED' ? '❌ Rechazada' : 
                         template.status === 'PAUSED' ? '⏸️ Pausada' : template.status}
                      </div>
                      <div className="template-language">Idioma: {template.category === 'MARKETING' ? `${template.language || 'es'} (Solo prueba)` : template.language || 'es'}</div>
                      {template.category && (
                        <div className="template-category">
                          Categoría: {template.category}
                          {template.category === 'MARKETING' && (
                            <span style={{ color: '#ff6b6b', fontSize: '11px', marginLeft: '8px' }}>
                              ⚠️ Solo números de prueba
                            </span>
                          )}
                        </div>
                      )}
                      {template.status === 'PENDING' && (
                        <div style={{ color: '#ffa500', fontSize: '11px', marginTop: '4px' }}>
                          ⚠️ Pendiente de aprobación
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {selectedTemplate && (
                <div className="template-preview">
                  <h3>Vista previa de la plantilla:</h3>
                  <div className="template-preview-content">
                    <div className="template-preview-name">
                      <strong>{selectedTemplate.name}</strong>
                    </div>
                    {selectedTemplate.components?.map((component, index) => {
                      if (component.type === 'HEADER') {
                        return (
                          <div key={index} className="template-component">
                            <strong>Encabezado:</strong> {component.format === 'TEXT' ? component.text : '[Media]'}
                          </div>
                        );
                      }
                      if (component.type === 'BODY') {
                        return (
                          <div key={index} className="template-component">
                            <strong>Cuerpo:</strong>
                            <div className="template-body-text">{component.text}</div>
                            {component.example?.body_text && component.example.body_text.length > 0 && (
                              <div className="template-params">
                                {component.example.body_text.map((example, i) => (
                                  <div key={i} className="template-param">
                                    <label>Parámetro {i + 1} (ejemplo: {example.join(', ')})</label>
                                    <input
                                      type="text"
                                      value={templateParams[`body_${i}`] || ''}
                                      onChange={(e) => setTemplateParams({
                                        ...templateParams,
                                        [`body_${i}`]: e.target.value
                                      })}
                                      placeholder={`Ingresa el valor para el parámetro ${i + 1}`}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      if (component.type === 'FOOTER') {
                        return (
                          <div key={index} className="template-component">
                            <strong>Pie de página:</strong> {component.text}
                          </div>
                        );
                      }
                      if (component.type === 'BUTTONS') {
                        return (
                          <div key={index} className="template-component">
                            <strong>Botones:</strong>
                            {component.buttons?.map((btn, btnIndex) => (
                              <div key={btnIndex} className="template-button">
                                {btn.type === 'QUICK_REPLY' && `🔘 ${btn.text}`}
                                {btn.type === 'URL' && `🔗 ${btn.text}`}
                                {btn.type === 'PHONE_NUMBER' && `📞 ${btn.text}`}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}

              {/* Opción manual solo como alternativa */}
              <div style={{ marginTop: '24px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9edef' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useManual}
                    onChange={(e) => setUseManual(e.target.checked)}
                  />
                  <span style={{ fontSize: '13px', color: '#667781' }}>O ingresar nombre de plantilla manualmente (si no aparece en la lista)</span>
                </label>
              </div>

              {useManual && (
                <div className="manual-template-input" style={{ marginTop: '16px' }}>
                  <div className="form-group">
                    <label>Nombre de la plantilla *</label>
                    <input
                      type="text"
                      value={manualTemplateName}
                      onChange={(e) => setManualTemplateName(e.target.value)}
                      placeholder="Ejemplo: bienvenida, confirmacion, etc."
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e9edef' }}
                    />
                    <small style={{ color: '#667781', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Ingresa el nombre exacto de tu plantilla aprobada (sin espacios, usa guiones bajos)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Idioma</label>
                    <select
                      value={manualLanguage}
                      onChange={(e) => setManualLanguage(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e9edef' }}
                    >
                      <option value="es">Español (es)</option>
                      <option value="en">Inglés (en)</option>
                      <option value="pt">Portugués (pt)</option>
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="template-modal-actions">
          <button onClick={onClose} className="cancel-button">
            Cancelar
          </button>
          {(selectedTemplate || useManual) && (
            <button
              onClick={handleSend}
              disabled={sending || !phoneNumber || (useManual && !manualTemplateName.trim())}
              className="send-template-button"
            >
              {sending ? 'Enviando...' : '📤 Enviar Plantilla'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;

